import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NavbarAuth from '../components/NavbarAuth';
import api from '../services/api';

// ═══ Hook responsive (identique à Profile) ═══
const useResponsiveLocal = () => {
    const getState = () => {
        const w = window.innerWidth;
        return { isSmall: w <= 480, isMobile: w <= 768, isTablet: w > 768 && w <= 968 };
    };
    const [state, setState] = useState(getState);
    useEffect(() => {
        const handleResize = () => setState(getState());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return state;
};

// ═══ Icônes SVG monochromes (identiques à Profile) ═══
const GOLD = '#c9a84c';
const Icon = ({ path, size = 18, color = GOLD }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>{path}</svg>
);

const icons = {
    mail: <><rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22,6 12,13 2,6" /></>,
    user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
    edit: <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></>,
    send: <><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></>,
    search: <><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,
    close: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
    arrowLeft: <><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></>,
    lock: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
    check: <polyline points="20 6 9 17 4 12" />,
    eye: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>,
    clock: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
    ban: <><circle cx="12" cy="12" r="10" /><line x1="4.9" y1="4.9" x2="19.1" y2="19.1" /></>,
    history: <><path d="M3 3v5h5" /><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" /><path d="M12 7v5l4 2" /></>,
};

// Longueur maximale d'un message, en caractères.
// Doit rester identique à MAX_CARACTERES_MESSAGE dans backend/src/controllers/messageController.js.
// Ici c'est du confort (compteur + blocage de la saisie) ; la vraie contrainte est côté serveur.
const MAX_CARACTERES = 300;

const Messages = () => {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [receiverId, setReceiverId] = useState(searchParams.get('to') || '');
    const [receiverName, setReceiverName] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [editingMessage, setEditingMessage] = useState(null);
    const [myPhoto, setMyPhoto] = useState(null);
    const [quota, setQuota] = useState(null);

    // ═══ État du panneau Messenger ═══
    const [panelOpen, setPanelOpen] = useState(true);
    const [activeConv, setActiveConv] = useState(null);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [historique, setHistorique] = useState([]);
    const [historiqueLoading, setHistoriqueLoading] = useState(false);
    const panelRef = useRef(null);

    const { isSmall, isMobile, isTablet } = useResponsiveLocal();
    const compact = isMobile || isTablet;

    useEffect(() => {
        fetchMessages();
        fetchMyProfile();
        fetchQuota();
    }, []);

    // Charge le prénom du destinataire dès que receiverId change
    useEffect(() => {
        const fetchReceiverName = async () => {
            if (!receiverId) { setReceiverName(''); return; }
            const id = parseInt(receiverId);
            const msg = messages.find(m => m.sender_id === id || m.receiver_id === id);
            if (msg) {
                const name = msg.sender_id === id
                    ? (msg.sender_name || msg.sender_email)
                    : (msg.receiver_name || msg.receiver_email);
                setReceiverName(name);
                return;
            }
            try {
                const response = await api.get(`/profiles/${id}`);
                if (response.data) {
                    setReceiverName(response.data.first_name || `Membre #${id}`);
                }
            } catch (err) {
                setReceiverName(`Membre #${id}`);
            }
        };
        fetchReceiverName();
    }, [receiverId, messages]);

    // Fermer le panneau au clic extérieur
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (e.target.closest('[data-messenger-toggle]')) return;
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setPanelOpen(false);
            }
        };
        if (panelOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [panelOpen]);

    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => { setSuccess(''); setError(''); }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    // Quand on arrive via le bouton "Contacter" (URL ?to=ID), ouvrir directement
    // la conversation avec ce membre, même si aucun message n'existe encore.
    useEffect(() => {
        if (receiverId) {
            setActiveConv(parseInt(receiverId));
        }
    }, [receiverId]);

    // Arrivée — ou nouvelle arrivée — via ?to=ID : on rouvre le panneau et on
    // sélectionne la conversation.
    //
    // L'effet ci-dessus ne suffit pas : `receiverId` n'est lu qu'au montage du
    // composant. Si le membre est déjà sur la page, ferme le panneau, puis
    // reclique sur la même notification, rien ne changerait. On se cale donc
    // sur location.key, qui change à chaque navigation même vers une adresse
    // identique.
    const location = useLocation();
    useEffect(() => {
        const to = new URLSearchParams(location.search).get('to');
        if (to) {
            setReceiverId(to);
            setActiveConv(parseInt(to));
            setPanelOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    const fetchMessages = async () => {
        try {
            const response = await api.get('/messages');
            setMessages(response.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchMyProfile = async () => {
        try {
            const response = await api.get(`/profiles/${user.id}`);
            if (response.data) setMyPhoto(response.data.photo_url);
        } catch (err) {
            console.error(err);
        }
    };

    // Quota quotidien de messages (null pour les admins → compteur masqué)
    const fetchQuota = async () => {
        try {
            const response = await api.get('/messages/quota');
            setQuota(response.data && response.data.quota === null ? null : response.data);
        } catch (err) {
            console.error(err);
        }
    };

    // Historique des échanges terminés — chargé à la demande
    const fetchHistorique = async () => {
        setHistoriqueLoading(true);
        try {
            const response = await api.get('/requests/historique');
            setHistorique(response.data);
        } catch (err) {
            console.error(err);
        } finally {
            setHistoriqueLoading(false);
        }
    };

    useEffect(() => {
        if (filter === 'historique' && historique.length === 0) fetchHistorique();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !receiverId) return;
        // Garde-fou : on n'envoie pas une requête vouée à être refusée par le serveur.
        if (newMessage.trim().length > MAX_CARACTERES) {
            setError(`Votre message est trop long (${newMessage.trim().length} caractères). La limite est de ${MAX_CARACTERES} caractères.`);
            return;
        }
        setSending(true);
        setError('');
        setSuccess('');
        try {
            const res = await api.post('/messages', {
                receiver_id: parseInt(receiverId),
                content: newMessage,
            });
            if (res.data && res.data.quota) setQuota(res.data.quota);
            setSuccess('Message envoyé ! En attente de validation par notre équipe.');
            setNewMessage('');
            fetchMessages();
        } catch (err) {
            // Envoi bloqué : hors des heures de modération, limite quotidienne atteinte,
            // ou autre message d'erreur renvoyé par le serveur.
            const data = err.response?.data;
            if (data?.limite_caracteres || data?.message_vide) {
                setError(data?.message || `Votre message dépasse ${MAX_CARACTERES} caractères.`);
            } else if (data?.hors_horaires || data?.limite_quotidienne || err.response?.status === 429) {
                setError(data?.message
                    || 'L\'envoi de messages n\'est pas possible pour le moment.');
            } else {
                setError(data?.message || 'Erreur lors de l\'envoi');
            }
        } finally {
            setSending(false);
        }
    };

    const handleMarkRead = async (id) => {
        try {
            await api.put(`/messages/${id}/read`);
            fetchMessages();
        } catch (err) {
            console.error(err);
        }
    };

    const handleResend = async (e) => {
        e.preventDefault();
        if (!editingMessage) return;
        // Même garde-fou que pour un envoi normal : un message rejeté que l'on
        // corrige doit lui aussi respecter la limite avant de repartir.
        if ((editingMessage.content || '').trim().length > MAX_CARACTERES) {
            setError(`Votre message est trop long (${editingMessage.content.trim().length} caractères). La limite est de ${MAX_CARACTERES} caractères.`);
            return;
        }
        setSending(true);
        setError('');
        setSuccess('');
        try {
            if (editingMessage.id) {
                await api.delete(`/messages/${editingMessage.id}/rejected`);
            }
            const res = await api.post('/messages', {
                receiver_id: editingMessage.receiver_id,
                content: editingMessage.content,
            });
            if (res.data && res.data.quota) setQuota(res.data.quota);
            setSuccess('Message renvoyé ! En attente de validation.');
            setEditingMessage(null);
            fetchMessages();
        } catch (err) {
            // On affiche le motif exact renvoyé par le serveur (message trop long,
            // hors horaires, quota atteint...) plutôt qu'une erreur générique.
            setError(err.response?.data?.message || 'Erreur lors du renvoi');
        } finally {
            setSending(false);
        }
    };

    // ═══ Regrouper les messages par interlocuteur ═══
    const conversations = {};
    messages.forEach(msg => {
        const isReceived = msg.receiver_id === user.id;
        const otherId = isReceived ? msg.sender_id : msg.receiver_id;
        const otherName = isReceived
            ? (msg.sender_name || msg.sender_email)
            : (msg.receiver_name || msg.receiver_email);
        const otherPhoto = isReceived ? msg.sender_photo : msg.receiver_photo;

        if (!conversations[otherId]) {
            conversations[otherId] = {
                otherId,
                otherName,
                otherPhoto,
                messages: [],
                unreadCount: 0,
                lastDate: msg.created_at,
            };
        }
        conversations[otherId].messages.push({ ...msg, isReceived });
        if (isReceived && msg.status === 'approved' && !msg.is_read) {
            conversations[otherId].unreadCount++;
        }
        if (new Date(msg.created_at) > new Date(conversations[otherId].lastDate)) {
            conversations[otherId].lastDate = msg.created_at;
            if (otherPhoto) conversations[otherId].otherPhoto = otherPhoto;
        }
    });

    Object.values(conversations).forEach(conv => {
        conv.messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    });

    let conversationList = Object.values(conversations).sort(
        (a, b) => new Date(b.lastDate) - new Date(a.lastDate)
    );

    if (filter === 'unread') {
        conversationList = conversationList.filter(c => c.unreadCount > 0);
    }
    if (search.trim()) {
        conversationList = conversationList.filter(c =>
            (c.otherName || '').toLowerCase().includes(search.toLowerCase())
        );
    }

    const unreadCount = messages.filter(
        msg => msg.receiver_id === user.id && msg.status === 'approved' && !msg.is_read
    ).length;

    // Conversation active : soit une existante (avec messages), soit une conversation
    // vide créée à la volée pour un nouveau membre contacté (via "Contacter"),
    // afin d'afficher le champ de saisie même sans message échangé.
    let currentConv = activeConv != null ? conversations[activeConv] : null;
    if (!currentConv && activeConv != null && receiverName) {
        currentConv = {
            otherId: activeConv,
            otherName: receiverName,
            otherPhoto: null,
            messages: [],
            unreadCount: 0,
            lastDate: new Date().toISOString(),
        };
    }

    const getPreview = (conv) => {
        const last = conv.messages[conv.messages.length - 1];
        if (!last) return '';
        const prefix = last.isReceived ? '' : 'Vous : ';
        // Un message que J'AI envoyé mais encore en modération : visible pour moi seul
        if (!last.isReceived && last.status === 'pending') return 'En attente de modération';
        if (!last.isReceived && last.status === 'rejected') return 'Message refusé';
        const txt = last.content.length > 28 ? last.content.slice(0, 28) + '...' : last.content;
        return prefix + txt;
    };

    // Date au format court pour l'historique (ex. 12 mars 2026)
    const formatDateCourte = (d) => {
        if (!d) return '—';
        return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const timeAgo = (date) => {
        const diff = Math.floor((Date.now() - new Date(date)) / 60000);
        if (diff < 1) return "à l'instant";
        if (diff < 60) return `${diff} min`;
        const h = Math.floor(diff / 60);
        if (h < 24) return `${h} h`;
        const d = Math.floor(h / 24);
        return `${d} j`;
    };

    const openConversation = (conv) => {
        setActiveConv(conv.otherId);
        setReceiverId(conv.otherId.toString());
        conv.messages.forEach(m => {
            if (m.isReceived && m.status === 'approved' && !m.is_read) {
                handleMarkRead(m.id);
            }
        });
    };

    const togglePanel = () => {
        setPanelOpen(prev => !prev);
        setActiveConv(null);
    };

    // ═══ Styles responsives ═══
    const layoutStyle = {
        padding: isSmall ? '12px' : isMobile ? '16px' : '32px',
        maxWidth: '760px',
        width: '100%',
        margin: '0 auto',
        boxSizing: 'border-box',
    };

    const cardStyle = {
        ...styles.card,
        padding: isSmall ? '16px' : isMobile ? '20px' : isTablet ? '28px' : '40px',
    };

    const panelStyle = {
        position: 'fixed',
        top: isMobile ? 0 : '100px',
        right: isMobile ? 0 : '24px',
        left: isMobile ? 0 : 'auto',
        bottom: isMobile ? 0 : 'auto',
        width: isMobile ? '100%' : '380px',
        maxWidth: '100vw',
        height: isMobile ? '100dvh' : '70vh',
        maxHeight: isMobile ? 'none' : '620px',
        background: 'linear-gradient(135deg, #fdf2f5, #faeef2)',
        borderRadius: isMobile ? 0 : '20px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: isMobile ? 'none' : '1px solid #ecd9b9',
        boxSizing: 'border-box',
    };

    const modalCardStyle = {
        background: 'linear-gradient(135deg, #fdf2f5, #faeef2)',
        padding: isSmall ? '20px' : '32px',
        borderRadius: '20px',
        width: '100%',
        maxWidth: '520px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        border: '1px solid #ecd9b9',
        boxSizing: 'border-box',
    };

    const bubbleReceivedStyle = {
        ...styles.bubbleReceived,
        maxWidth: isMobile ? '85%' : '75%',
    };

    const bubbleSentStyle = {
        ...styles.bubbleSent,
        maxWidth: isMobile ? '85%' : '75%',
    };

    // Label réutilisable (identique à Profile)
    const Label = ({ icon, children }) => (
        <label style={styles.label}><span style={styles.labelIcon}><Icon path={icon} size={16} /></span>{children}</label>
    );

    return (
        <div style={styles.page}>
            <NavbarAuth
                myPhoto={myPhoto}
                unreadCount={unreadCount}
                onMessagesClick={togglePanel}
            />

            {/* ═══ Panneau Messenger ═══ */}
            <div ref={panelRef}>
                {panelOpen && (
                    <div style={panelStyle}>
                        {/* Vue LISTE des conversations */}
                        {!currentConv && (
                            <>
                                <div style={styles.panelHeader}>
                                    <h3 style={styles.panelTitle}>
                                        <Icon path={icons.mail} size={20} color="#4a0028" /> Discussions
                                    </h3>
                                    <button style={styles.closeBtn} onClick={() => setPanelOpen(false)}>
                                        <Icon path={icons.close} size={16} color="#4a0028" />
                                    </button>
                                </div>

                                <div style={styles.searchWrap}>
                                    <input
                                        type="text"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Rechercher une discussion..."
                                        style={styles.searchInput}
                                    />
                                </div>

                                <div style={styles.filterTabs}>
                                    <button
                                        style={filter === 'all' ? styles.filterActive : styles.filterTab}
                                        onClick={() => setFilter('all')}
                                    >
                                        Tout
                                    </button>
                                    <button
                                        style={filter === 'unread' ? styles.filterActive : styles.filterTab}
                                        onClick={() => setFilter('unread')}
                                    >
                                        Non lu
                                    </button>
                                    <button
                                        style={filter === 'historique' ? styles.filterActive : styles.filterTab}
                                        onClick={() => setFilter('historique')}
                                    >
                                        Historique
                                    </button>
                                </div>

                                <div style={styles.convScroll}>
                                    {filter === 'historique' ? (
                                        // ═══ Historique des échanges terminés ═══
                                        historiqueLoading ? (
                                            <div style={styles.loading}>Chargement...</div>
                                        ) : historique.length === 0 ? (
                                            <div style={styles.empty}>Aucun échange terminé.</div>
                                        ) : (
                                            historique.map(h => (
                                                <div key={h.id} style={styles.histoItem}>
                                                    <div style={styles.histoHead}>
                                                        <div style={styles.convAvatar}>
                                                            {h.autre_photo ? (
                                                                <img src={h.autre_photo} alt="" style={styles.convAvatarImg} />
                                                            ) : (
                                                                <Icon path={icons.user} size={20} color="#c0306a" />
                                                            )}
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <span style={styles.histoNom}>{h.autre_prenom || 'Membre'}</span>
                                                            <span style={styles.histoDates}>
                                                                {formatDateCourte(h.date_debut)} → {formatDateCourte(h.date_fin)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <span style={h.termine_par_moi ? styles.histoBadgeMoi : styles.histoBadgeAutre}>
                                                        {h.termine_par_moi
                                                            ? 'Terminé par vous'
                                                            : `Terminé par ${h.autre_prenom || 'ce membre'}`}
                                                    </span>
                                                    {h.motif_fin && (
                                                        <div style={styles.histoMotif}>
                                                            <strong>Motif :</strong> {h.motif_fin}
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        )
                                    ) : loading ? (
                                        <div style={styles.loading}>Chargement...</div>
                                    ) : conversationList.length === 0 ? (
                                        <div style={styles.empty}>Aucune conversation.</div>
                                    ) : (
                                        conversationList.map(conv => (
                                            <div
                                                key={conv.otherId}
                                                style={styles.convItem}
                                                onClick={() => openConversation(conv)}
                                            >
                                                <div style={styles.convAvatar}>
                                                    {conv.otherPhoto ? (
                                                        <img src={conv.otherPhoto} alt="" style={styles.convAvatarImg} />
                                                    ) : (
                                                        <Icon path={icons.user} size={22} color="#c9a84c" />
                                                    )}
                                                </div>
                                                <div style={styles.convInfo}>
                                                    <span style={conv.unreadCount > 0 ? styles.convNameBold : styles.convName}>
                                                        {conv.otherName}
                                                    </span>
                                                    <div style={styles.convPreviewRow}>
                                                        {(() => { const lm = conv.messages[conv.messages.length - 1]; if (lm && !lm.isReceived && lm.status === 'pending') return <Icon path={icons.lock} size={11} color="#c0306a" />; if (lm && !lm.isReceived && lm.status === 'rejected') return <Icon path={icons.ban} size={11} color="#c00" />; return null; })()}
                                                        <span style={conv.unreadCount > 0 ? styles.convPreviewBold : styles.convPreview}>
                                                            {getPreview(conv)}
                                                        </span>
                                                        <span style={styles.convTime}>· {timeAgo(conv.lastDate)}</span>
                                                    </div>
                                                </div>
                                                {conv.unreadCount > 0 && <span style={styles.convDot} />}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </>
                        )}

                        {/* Vue CHAT (conversation ouverte) */}
                        {currentConv && (
                            <>
                                <div style={styles.chatHeader}>
                                    <button style={styles.backBtn} onClick={() => setActiveConv(null)}>
                                        <Icon path={icons.arrowLeft} size={18} color="#4a0028" />
                                    </button>
                                    <div style={styles.convAvatarSmall}>
                                        {currentConv.otherPhoto ? (
                                            <img src={currentConv.otherPhoto} alt="" style={styles.convAvatarImg} />
                                        ) : (
                                            <Icon path={icons.user} size={18} color="#c9a84c" />
                                        )}
                                    </div>
                                    <div style={styles.chatHeaderInfo}>
                                        <span style={styles.chatName}>{currentConv.otherName}</span>
                                        {quota && (
                                            <span style={styles.quotaCompteur}>
                                                Vous avez {quota.restants} message{quota.restants > 1 ? 's' : ''} restant{quota.restants > 1 ? 's' : ''} pour aujourd'hui
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div style={styles.chatBody}>
                                    {currentConv.messages.map(msg => {
                                        const isReceived = msg.isReceived;
                                        return (
                                            <div
                                                key={msg.id}
                                                style={isReceived ? bubbleReceivedStyle : bubbleSentStyle}
                                            >
                                                {isReceived && msg.status !== 'approved' ? (
                                                    <p style={{ ...styles.messageHidden, color: '#a86b8a' }}>
                                                        <Icon path={icons.lock} size={12} color="#c0306a" /> En attente de modération
                                                    </p>
                                                ) : (
                                                    <p style={isReceived ? styles.bubbleContent : styles.bubbleContentSent}>
                                                        {msg.content}
                                                    </p>
                                                )}
                                                <span style={isReceived ? styles.bubbleDate : styles.bubbleDateSent}>
                                                    {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                                </span>

                                                <div style={styles.statusBadge}>
                                                    {msg.status === 'pending' && (
                                                        <span style={styles.statusPending}>
                                                            <Icon path={icons.clock} size={11} color="#4a0028" /> En attente de modération
                                                        </span>
                                                    )}

                                                    {!isReceived && msg.status === 'approved' && (
                                                        msg.is_read
                                                            ? <span style={styles.statusReadSent}>
                                                                <Icon path={icons.eye} size={11} color="#4a0028" /> Lu
                                                            </span>
                                                            : <span style={styles.statusApprovedSent}>
                                                                <Icon path={icons.check} size={11} color="#4a0028" /> Transmis
                                                            </span>
                                                    )}

                                                    {!isReceived && msg.status === 'rejected' && (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                                <span style={styles.statusRejectedSent}>
                                                                    <Icon path={icons.ban} size={11} color="#ffb3b3" /> Rejeté
                                                                </span>
                                                                {/* Bouton Modifier masqué (fonction conservée) — remplacer false par true pour réafficher */}
                                                                {false && (
                                                                    <button
                                                                        onClick={() => setEditingMessage({
                                                                            id: msg.id,
                                                                            receiver_id: msg.receiver_id,
                                                                            content: msg.content,
                                                                            receiver_email: msg.receiver_email
                                                                        })}
                                                                        style={styles.resendBtn}
                                                                    >
                                                                        <Icon path={icons.edit} size={11} color="#f3e0c7" /> Modifier
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {msg.motif_rejet && (
                                                                <div style={styles.motifBoxSent}>
                                                                    <strong>Motif du refus :</strong> {msg.motif_rejet}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {isReceived && msg.status === 'rejected' && (
                                                        <span style={styles.statusRejected}>
                                                            <Icon path={icons.ban} size={11} color="#c00" /> Rejeté
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Avertissement / confirmation affiché au-dessus du champ de saisie.
                                    C'est ici que s'affiche notamment la limite de messages en attente de modération. */}
                                {error && (
                                    <div style={{
                                        margin: '0 14px 8px', padding: '10px 14px',
                                        backgroundColor: '#fff4e0', color: '#8a5a00',
                                        border: '1px solid #f0d9a8', borderRadius: '10px',
                                        fontSize: '12.5px', lineHeight: '1.5',
                                        display: 'flex', alignItems: 'flex-start', gap: '8px',
                                    }}>
                                        <Icon path={icons.clock} size={14} color="#4a0028" />
                                        <span>{error}</span>
                                    </div>
                                )}
                                {success && (
                                    <div style={{
                                        margin: '0 14px 8px', padding: '10px 14px',
                                        backgroundColor: '#f0fff0', color: '#2c5f2e',
                                        border: '1px solid #b8e0b8', borderRadius: '10px',
                                        fontSize: '12.5px', lineHeight: '1.5',
                                        display: 'flex', alignItems: 'flex-start', gap: '8px',
                                    }}>
                                        <Icon path={icons.check} size={14} color="#2c5f2e" />
                                        <span>{success}</span>
                                    </div>
                                )}

                                {/* Compteur de caractères : passe en prune quand il reste peu de marge,
                                    pour prévenir avant que la saisie ne se bloque. */}
                                <div style={styles.compteurWrap}>
                                    <span style={{
                                        ...styles.compteurCar,
                                        color: newMessage.length >= MAX_CARACTERES
                                            ? '#c0306a'
                                            : newMessage.length > MAX_CARACTERES * 0.85
                                                ? '#8a5a00'
                                                : '#9a7d88',
                                    }}>
                                        {newMessage.length} / {MAX_CARACTERES}
                                    </span>
                                </div>

                                <form style={styles.chatInputWrap} onSubmit={handleSend}>
                                    <textarea
                                        rows={1}
                                        maxLength={MAX_CARACTERES}
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        placeholder="Écrivez un message..."
                                        style={styles.chatInput}
                                    />
                                    <button type="submit" style={styles.chatSendBtn} disabled={sending}>
                                        <Icon path={icons.send} size={16} color="#f3e0c7" />
                                    </button>
                                </form>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* ═══ Contenu principal ═══ */}
            <div style={layoutStyle}>
                <main style={cardStyle}>
                    <div style={styles.titleRow}>
                        <h2 style={{ ...styles.title, fontSize: isSmall ? '22px' : isMobile ? '25px' : '30px', margin: 0 }}>
                            <span style={styles.titleIcon}>
                                <Icon path={icons.mail} size={isSmall ? 20 : 26} color="#c0306a" />
                            </span>
                            Ma messagerie
                        </h2>
                    </div>
                    <p style={styles.subtitle}>
                        Vos discussions s'ouvrent dans le panneau à droite. Vous y retrouvez vos
                        échanges en cours, vos messages non lus, ainsi que l'historique de vos
                        échanges terminés.
                    </p>

                    {/* Comment démarrer une conversation */}
                    <div style={styles.infoBloc}>
                        <h3 style={styles.infoTitre}>
                            <Icon path={icons.user} size={17} color="#c0306a" /> Démarrer une conversation
                        </h3>
                        <p style={styles.infoTexte}>
                            Pour échanger avec un membre, une demande de mise en relation doit d'abord
                            être acceptée des deux côtés. Une fois la relation établie, votre conversation
                            apparaît dans l'onglet « Tout » du panneau de discussions.
                        </p>
                    </div>

                    {/* Modération */}
                    <div style={styles.infoBloc}>
                        <h3 style={styles.infoTitre}>
                            <Icon path={icons.lock} size={17} color="#c0306a" /> La modération des messages
                        </h3>
                        <p style={styles.infoTexte}>
                            Par souci de respect et de sérieux, chaque message est vérifié par notre
                            équipe avant d'être transmis à son destinataire, incha'Allah.
                        </p>

                        <div style={styles.infoLigne}>
                            <span style={styles.puceAttente}><Icon path={icons.clock} size={13} color="#4a0028" /></span>
                            <span style={styles.infoTexte}>
                                <strong>En attente de modération</strong> — votre message vient d'être
                                envoyé. Il n'est pas encore visible par le destinataire.
                            </span>
                        </div>

                        <div style={styles.infoLigne}>
                            <span style={styles.puceOk}><Icon path={icons.check} size={13} color="#2c5f2e" /></span>
                            <span style={styles.infoTexte}>
                                <strong>Message accepté</strong> — votre message a été validé et transmis.
                                Il apparaît alors dans la conversation de votre correspondant.
                            </span>
                        </div>

                        <div style={styles.infoLigne}>
                            <span style={styles.puceRefus}><Icon path={icons.ban} size={13} color="#c00" /></span>
                            <span style={styles.infoTexte}>
                                <strong>Message refusé</strong> — si un message ne respecte pas le cadre
                                du site, il est refusé et n'est pas transmis. Le motif du refus vous est
                                indiqué directement sous votre message.
                            </span>
                        </div>

                        {/* Texte d'aide "Modifier un message refusé" masqué — remplacer false par true pour réafficher */}
                        {false && (
                            <div style={styles.infoLigne}>
                                <span style={styles.puceEdit}><Icon path={icons.edit} size={13} color="#8b1a4a" /></span>
                                <span style={styles.infoTexte}>
                                    <strong>Modifier un message refusé</strong> — après un refus, vous pouvez
                                    corriger votre message grâce au bouton « Modifier », puis le renvoyer. Il
                                    repassera alors par la modération.
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Clôture d'un échange */}
                    <div style={styles.infoBloc}>
                        <h3 style={styles.infoTitre}>
                            <Icon path={icons.ban} size={17} color="#c0306a" /> La clôture d'un échange
                        </h3>
                        <p style={styles.infoTexte}>
                            Chacun des deux membres peut mettre fin à l'échange à tout moment, depuis
                            le tableau de bord. Un motif doit obligatoirement être indiqué : il sera
                            visible par l'autre personne.
                        </p>

                        <div style={styles.infoLigne}>
                            <span style={styles.puceEdit}><Icon path={icons.clock} size={13} color="#c0306a" /></span>
                            <span style={styles.infoTexte}>
                                <strong>Messages en attente</strong> — si un message était encore en
                                modération au moment de la clôture, il ne sera pas transmis.
                            </span>
                        </div>

                        <div style={styles.infoLigne}>
                            <span style={styles.puceOk}><Icon path={icons.check} size={13} color="#c0306a" /></span>
                            <span style={styles.infoTexte}>
                                <strong>Retour à l'annuaire</strong> — une fois l'échange terminé, vous
                                retrouvez tous les deux l'accès aux autres profils.
                            </span>
                        </div>

                        <div style={styles.infoLigne}>
                            <span style={styles.puceAttente}><Icon path={icons.history} size={13} color="#c0306a" /></span>
                            <span style={styles.infoTexte}>
                                <strong>Historique conservé</strong> — la trace de l'échange (dates et
                                motif) reste consultable dans l'onglet « Historique » du panneau.
                            </span>
                        </div>
                    </div>

                    <p style={styles.infoNote}>
                        Nous vous remercions de préserver des échanges respectueux et conformes aux
                        valeurs de la communauté.
                    </p>
                </main>
            </div>

            {/* Modal modification message rejeté */}
            {editingMessage && (
                <div style={styles.modal}>
                    <div style={modalCardStyle}>
                        <h3 style={styles.modalTitle}>
                            <Icon path={icons.edit} size={20} color="#4a0028" /> Modifier votre message
                        </h3>
                        <p style={styles.modalSubtitle}>
                            À : <strong>{editingMessage.receiver_email}</strong>
                        </p>
                        <form onSubmit={handleResend}>
                            <textarea
                                value={editingMessage.content}
                                maxLength={MAX_CARACTERES}
                                onChange={(e) => setEditingMessage({
                                    ...editingMessage,
                                    content: e.target.value
                                })}
                                style={styles.modalTextarea}
                                rows="5"
                                required
                            />
                            {/* Un ancien message rejeté peut dépasser la limite : on le signale
                                clairement au lieu de laisser le renvoi échouer sans explication. */}
                            <div style={styles.compteurWrapModal}>
                                <span style={{
                                    ...styles.compteurCar,
                                    color: (editingMessage.content || '').length > MAX_CARACTERES
                                        ? '#c0306a'
                                        : (editingMessage.content || '').length > MAX_CARACTERES * 0.85
                                            ? '#8a5a00'
                                            : '#9a7d88',
                                }}>
                                    {(editingMessage.content || '').length} / {MAX_CARACTERES}
                                    {(editingMessage.content || '').length > MAX_CARACTERES
                                        && ' — veuillez raccourcir votre message'}
                                </span>
                            </div>
                            <div style={styles.modalBtns}>
                                <button
                                    type="button"
                                    onClick={() => setEditingMessage(null)}
                                    style={styles.modalCancelBtn}
                                >
                                    Annuler
                                </button>
                                <button type="submit" style={styles.modalSendBtn} disabled={sending}>
                                    {sending ? 'Envoi...' : 'Renvoyer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    // ═══ Base (identique à Profile) ═══
    page: { minHeight: '100vh', width: '100%', boxSizing: 'border-box', overflowX: 'hidden', background: 'linear-gradient(160deg, #fdfdfe 0%, #f5f6f8 50%, #eef0f3 100%)', backgroundAttachment: 'fixed', fontFamily: 'Arial, sans-serif' },
    card: { background: '#ffffff', borderRadius: '24px', boxShadow: '0 12px 45px rgba(60,60,80,0.10)', border: '1px solid #eceef1', minWidth: 0 },
    title: { color: '#c0306a', fontWeight: '800', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: '12px' },
    titleIcon: { display: 'flex', alignItems: 'center' },
    subtitle: { color: '#a86a86', fontSize: '13.5px', lineHeight: '1.6', margin: '0 0 24px' },
    titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', marginBottom: '10px' },
    openDiscussionBtn: { position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 20px', background: '#fff', color: '#c0306a', border: '1.5px solid #f5d0e0', borderRadius: '30px', cursor: 'pointer', fontWeight: '700', fontSize: '14px', boxShadow: '0 4px 14px rgba(192,48,106,0.12)', whiteSpace: 'nowrap' },
    openDiscussionBadge: { backgroundColor: '#c0306a', color: '#fff', borderRadius: '50%', minWidth: '20px', height: '20px', fontSize: '11px', fontWeight: '800', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px', marginLeft: '2px' },

    // ═══ Blocs informatifs (page messagerie) ═══
    infoBloc: { background: 'linear-gradient(135deg, #fafbfc, #f2f4f7)', border: '1px solid #e8ebef', borderRadius: '18px', padding: '20px 22px', marginBottom: '18px', boxShadow: '0 4px 18px rgba(60,60,80,0.05)' },
    infoTitre: { display: 'flex', alignItems: 'center', gap: '9px', color: '#c0306a', fontSize: '16px', fontWeight: '800', margin: '0 0 12px' },
    infoTexte: { color: '#7a5665', fontSize: '13.5px', lineHeight: '1.65', margin: 0 },
    infoLigne: { display: 'flex', alignItems: 'flex-start', gap: '11px', marginTop: '14px' },
    puceAttente: { width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(74,0,40,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' },
    puceOk: { width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(44,95,46,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' },
    puceRefus: { width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(204,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' },
    puceEdit: { width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(192,48,106,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' },
    infoNote: { color: '#a86a86', fontSize: '13px', fontStyle: 'italic', lineHeight: '1.6', textAlign: 'center', margin: '4px 0 0', padding: '0 10px' },

    field: { display: 'flex', flexDirection: 'column', marginBottom: '20px' },
    label: { color: '#7a1340', fontWeight: '700', marginBottom: '8px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' },
    labelIcon: { display: 'flex', alignItems: 'center' },
    input: { padding: '13px 15px', borderRadius: '10px', border: '1.5px solid #e6cfa8', fontSize: '14.5px', outline: 'none', backgroundColor: '#fff', color: '#3a3a3a', boxSizing: 'border-box', width: '100%' },
    inputDisabled: { padding: '13px 15px', borderRadius: '10px', border: '1.5px solid #e6cfa8', fontSize: '14.5px', outline: 'none', backgroundColor: 'rgba(201,168,76,0.08)', color: '#7a1340', fontWeight: '700', boxSizing: 'border-box', width: '100%', cursor: 'not-allowed' },
    textarea: { width: '100%', padding: '13px 15px', borderRadius: '10px', border: '1.5px solid #e6cfa8', fontSize: '14.5px', outline: 'none', resize: 'vertical', backgroundColor: '#fff', color: '#3a3a3a', boxSizing: 'border-box' },

    navBtns: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '32px', paddingTop: '20px', borderTop: '1px solid #f0d9e3', overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
    submitBtn: { display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '14px 32px', background: '#c0306a', color: '#f3e0c7', border: 'none', borderRadius: '12px', fontSize: '16px', cursor: 'pointer', fontWeight: '700', boxShadow: '0 4px 16px rgba(74,0,40,0.3)', flexShrink: 0, whiteSpace: 'nowrap' },

    success: { backgroundColor: '#f0fff0', color: '#2c5f2e', padding: '12px', borderRadius: '10px', marginBottom: '16px', textAlign: 'center', border: '1px solid #c9a84c' },
    error: { backgroundColor: '#ffe0e0', color: '#c00', padding: '12px', borderRadius: '10px', marginBottom: '16px', textAlign: 'center' },
    loading: { textAlign: 'center', color: '#4a0028', padding: '20px', fontWeight: '600' },
    empty: { textAlign: 'center', color: '#9a7d88', fontStyle: 'italic', padding: '20px' },

    // ═══ Panneau Messenger ═══
    panelHeader: { padding: '18px 20px 10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    panelTitle: { color: '#4a0028', fontSize: '20px', fontWeight: '800', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' },
    closeBtn: { background: 'rgba(201,168,76,0.15)', border: '1px solid #ecd9b9', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 },
    searchWrap: { padding: '4px 16px 10px 16px' },
    searchInput: { width: '100%', padding: '11px 15px', borderRadius: '10px', border: '1.5px solid #e6cfa8', backgroundColor: '#fff', fontSize: '14px', boxSizing: 'border-box', outline: 'none', color: '#3a3a3a' },
    filterTabs: { display: 'flex', gap: '8px', padding: '0 16px 12px 16px' },
    // ═══ Historique dans le panneau ═══
    histoItem: { background: '#fafbfc', border: '1px solid #eceef1', borderRadius: '12px', padding: '12px 14px', margin: '0 10px 10px 10px' },
    histoHead: { display: 'flex', alignItems: 'center', gap: '11px', marginBottom: '8px' },
    histoNom: { display: 'block', color: '#c0306a', fontSize: '14.5px', fontWeight: '800' },
    histoDates: { display: 'block', color: '#9aa0a8', fontSize: '11.5px', marginTop: '2px' },
    histoBadgeMoi: { display: 'inline-block', backgroundColor: 'rgba(192,48,106,0.1)', color: '#c0306a', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' },
    histoBadgeAutre: { display: 'inline-block', backgroundColor: 'rgba(60,60,80,0.07)', color: '#7a8088', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' },
    histoMotif: { marginTop: '9px', paddingTop: '9px', borderTop: '1px solid #eceef1', color: '#5a6068', fontSize: '12.5px', lineHeight: '1.5' },
    filterTab: { padding: '7px 16px', borderRadius: '30px', border: '1.5px solid #e6cfa8', backgroundColor: '#fff', color: '#8b1a4a', fontSize: '13px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 },
    filterActive: { padding: '7px 16px', borderRadius: '30px', border: '1.5px solid #4a0028', background: '#c0306a', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', flexShrink: 0, boxShadow: '0 4px 14px rgba(74,0,40,0.3)' },

    convScroll: { flex: 1, overflowY: 'auto', padding: '0 10px 10px 10px' },
    convItem: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', borderRadius: '12px', cursor: 'pointer', transition: 'background 0.15s', border: '1px solid transparent' },
    convAvatar: { width: '48px', height: '48px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0, backgroundColor: '#4a0028', border: '2px solid #c0306a', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    convAvatarSmall: { width: '38px', height: '38px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0, backgroundColor: '#4a0028', border: '2px solid #c0306a', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    convAvatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
    convInfo: { flex: 1, minWidth: 0 },
    convName: { color: '#c0306a', fontSize: '15px', fontWeight: '600', display: 'block' },
    convNameBold: { color: '#c0306a', fontSize: '15px', fontWeight: '800', display: 'block' },
    convPreviewRow: { display: 'flex', alignItems: 'center', gap: '4px' },
    convPreview: { color: '#9a7d88', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    convPreviewBold: { color: '#5a4a52', fontSize: '13px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    convTime: { color: '#c0a8b4', fontSize: '12px', whiteSpace: 'nowrap', flexShrink: 0 },
    convDot: { width: '11px', height: '11px', borderRadius: '50%', backgroundColor: '#c9a84c', flexShrink: 0 },

    chatHeader: { display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', borderBottom: '1px solid #ecd9b9', background: 'rgba(201,168,76,0.06)' },
    backBtn: { background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', flexShrink: 0 },
    chatName: { color: '#c0306a', fontSize: '16px', fontWeight: '800' },
    chatHeaderInfo: { display: 'flex', flexDirection: 'column', gap: '1px' },
    compteurWrap: { display: 'flex', justifyContent: 'flex-end', padding: '0 16px 2px' },
    compteurWrapModal: { display: 'flex', justifyContent: 'flex-end', marginTop: '6px' },
    compteurCar: { fontSize: '11px', fontWeight: '600', letterSpacing: '0.2px' },
    quotaCompteur: { color: '#9a7d88', fontSize: '11px', fontWeight: '600' },
    chatBody: { flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' },
    bubbleReceived: { alignSelf: 'flex-start', backgroundColor: '#fff', border: '1px solid #ecd9b9', padding: '10px 14px', borderRadius: '14px 14px 14px 4px', wordBreak: 'break-word', overflowWrap: 'break-word' },
    bubbleSent: { alignSelf: 'flex-end', background: '#c0306a', padding: '10px 14px', borderRadius: '14px 14px 4px 14px', wordBreak: 'break-word', overflowWrap: 'break-word', boxShadow: '0 3px 10px rgba(74,0,40,0.2)' },
    bubbleContent: { color: '#3a3a3a', fontSize: '14px', margin: '0 0 3px 0', wordBreak: 'break-word', overflowWrap: 'break-word' },
    bubbleContentSent: { color: '#fff', fontSize: '14px', margin: '0 0 3px 0', wordBreak: 'break-word', overflowWrap: 'break-word' },
    bubbleDate: { color: '#c0a8b4', fontSize: '10px' },
    bubbleDateSent: { color: 'rgba(243,224,199,0.7)', fontSize: '10px' },
    messageHidden: { color: '#9a7d88', fontSize: '13px', fontStyle: 'italic', margin: '0 0 3px 0', display: 'flex', alignItems: 'center', gap: '5px' },
    statusBadge: { marginTop: '3px' },
    statusPending: { color: '#4a0028', fontWeight: '700', fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px' },
    statusRejected: { color: '#c00', fontWeight: '700', fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px' },
    statusApprovedSent: { color: '#4a0028', fontWeight: '700', fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px' },
    statusReadSent: { color: '#4a0028', fontWeight: '700', fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px' },
    statusRejectedSent: { color: '#ffb3b3', fontWeight: '700', fontSize: '11px', display: 'inline-flex', alignItems: 'center', gap: '4px' },
    resendBtn: { padding: '4px 10px', background: '#c0306a', color: '#f3e0c7', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' },
    // Motif de rejet affiché à l'expéditeur (bulle envoyée, fond sombre)
    motifBoxSent: { backgroundColor: 'rgba(255,255,255,0.15)', color: '#ffe0e6', fontSize: '11.5px', lineHeight: '1.45', padding: '7px 10px', borderRadius: '8px', borderLeft: '2px solid #ffb3b3' },

    chatInputWrap: { display: 'flex', gap: '10px', padding: '12px 14px', borderTop: '1px solid #ecd9b9', alignItems: 'center', background: 'rgba(201,168,76,0.06)' },
    chatInput: { flex: 1, padding: '11px 15px', borderRadius: '20px', border: '1.5px solid #e6cfa8', backgroundColor: '#fff', fontSize: '14px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', color: '#3a3a3a', minWidth: 0, resize: 'none', maxHeight: '120px', lineHeight: '1.4' },
    chatSendBtn: { width: '42px', height: '42px', borderRadius: '50%', background: '#c0306a', border: 'none', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(74,0,40,0.3)' },

    // ═══ Modale (identique à Profile) ═══
    modal: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px', boxSizing: 'border-box' },
    modalTitle: { color: '#4a0028', margin: '0 0 8px', fontSize: '20px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '10px' },
    modalSubtitle: { color: '#9a7d88', margin: '0 0 18px', fontSize: '13.5px' },
    modalTextarea: { width: '100%', padding: '13px 15px', borderRadius: '10px', border: '1.5px solid #e6cfa8', fontSize: '14.5px', resize: 'vertical', boxSizing: 'border-box', marginBottom: '18px', backgroundColor: '#fff', color: '#3a3a3a', outline: 'none' },
    modalBtns: { display: 'flex', gap: '14px' },
    modalCancelBtn: { flex: 1, padding: '13px', backgroundColor: '#fff', color: '#8b1a4a', border: '1.5px solid #e6cfa8', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' },
    modalSendBtn: { flex: 1, padding: '13px', background: '#c0306a', color: '#f3e0c7', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', fontSize: '14px', boxShadow: '0 4px 16px rgba(74,0,40,0.3)' },
};
export default Messages;